import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiEdit2,
  FiPlus,
  FiPlusCircle,
  FiSearch,
  FiRotateCcw,
  FiFilter,
} from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { Calculator } from "lucide-react";
import Swal from "sweetalert2";
import GlobalPagination from "../../../../Components/Pagination/GlobalPagination";
import SimilarItemsModal from "../../../../Components/Modals/SimilarItems/SimilarItems";
import {
  usePermission,
  SystemPermissions,
} from "../../../../Utils/permissionAccess";
import { useBlog } from "../../useHook";
import { HOUSE_COMPARISON_CATEGORY } from "../comparisonBlogConfig";
import "../../blog/style.css";

const ComparisonBlogList = () => {
  const navigate = useNavigate();
  const { getBlogs, deleteBlog, searchBlogs } = useBlog();

  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.Blogs.CREATE);
  const canUpdate = hasPermission(SystemPermissions.Blogs.UPDATE);
  const canDelete = hasPermission(SystemPermissions.Blogs.DELETE);
  const canSimilar = hasPermission(SystemPermissions.SimilarItems.CREATE);
  const canShowActions = canUpdate || canDelete || canSimilar;

  const [blogs, setBlogs] = useState([]);
  const [nameQuery, setNameQuery] = useState("");
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedBlogForSimilar, setSelectedBlogForSimilar] = useState(null);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    offset: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const loadData = async (searchParams = {}) => {
    try {
      const response = await getBlogs(
        { offset: pagination.offset, limit: pagination.limit },
        {
          categoryName: HOUSE_COMPARISON_CATEGORY,
          ...searchParams,
        },
      );
      setBlogs(response.data || []);
      if (response.pagination) {
        setPagination((p) => ({ ...p, ...response.pagination }));
      }
    } catch (error) {
      console.error("Error fetching comparison blogs:", error);
    }
  };

  useEffect(() => {
    loadData();
  }, [pagination.offset, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    try {
      const searchParams = { categoryName: HOUSE_COMPARISON_CATEGORY };
      if (nameQuery.trim()) searchParams.name = nameQuery.trim();
      const response = await searchBlogs(
        { offset: 0, limit: pagination.limit },
        searchParams,
      );
      setBlogs(response.data || []);
      if (response.pagination) {
        setPagination((p) => ({ ...p, ...response.pagination, offset: 0 }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleReset = () => {
    setNameQuery("");
    setPagination((p) => ({ ...p, offset: 0 }));
    loadData();
  };

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  const handleDelete = (blogId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#982598",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteBlog(blogId);
        Swal.fire({
          title: "Deleted!",
          text: "Comparison blog has been deleted successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        loadData();
      } catch (e) {
        Swal.fire({
          title: "Error!",
          text: "Failed to delete comparison blog.",
          icon: "error",
        });
      }
    });
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Comparison Blogs</h2>
          <p className="text-xs text-gray-500">
            Linked A vs B cost tables · category “{HOUSE_COMPARISON_CATEGORY}”
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-filter filter-btn"
            onClick={() => navigate("/comparison-blogs")}
          >
            <Calculator size={14} />
            Preview tool
          </button>
          {canCreate && (
            <button
              type="button"
              onClick={() => navigate("/comparison-blogs/create")}
              className="btn btn-primary h-[44px]"
            >
              <FiPlus className="text-white" />
              Create Comparison Blog
            </button>
          )}
        </div>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by blog name"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Name</th>
                <th className="table-th">Status</th>
                {canShowActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {blogs?.length > 0 ? (
                blogs.map((blog, index) => (
                  <tr
                    key={blog.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      {pagination.offset + index + 1}
                    </td>
                    <td className="table-td">
                      <div className="font-semibold text-gray-900">
                        {blog.name}
                      </div>
                    </td>
                    <td className="table-td">
                      <span
                        className={`px-2 py-1 rounded-full text-sm ${
                          blog.status === "published"
                            ? "bg-green-200 text-green-800"
                            : "bg-yellow-200 text-yellow-800"
                        }`}
                      >
                        {blog.status === "published" ? "Published" : "Draft"}
                      </span>
                    </td>
                    {canShowActions && (
                      <td className="table-td flex flex-wrap gap-2 space-x-0">
                        {canSimilar && (
                          <button
                            type="button"
                            className="btn btn-action"
                            onClick={() => {
                              setSelectedBlogForSimilar(blog.id);
                              setIsSimilarModalOpen(true);
                            }}
                          >
                            <FiPlusCircle className="text-white" />
                            Similar Items
                          </button>
                        )}
                        {canUpdate && (
                          <button
                            type="button"
                            className="btn btn-edit text-white"
                            onClick={() =>
                              navigate(`/comparison-blogs/edit/${blog.id}`)
                            }
                          >
                            <FiEdit2 className="text-white" /> Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="btn btn-delete text-white"
                            onClick={() => handleDelete(blog.id)}
                          >
                            <MdDelete className="text-white" /> Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr className="bg-gray-50 hover:bg-gray-100 transition-colors duration-300">
                  <td
                    colSpan={canShowActions ? 4 : 3}
                    className="px-6 py-4 text-center text-sm text-gray-700"
                  >
                    No comparison blogs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <GlobalPagination
            offset={pagination.offset}
            limit={pagination.limit}
            total={pagination.totalCount}
            onPageChange={handlePageChange}
            className=""
          />
        </div>
      )}

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={() => {
            setIsSimilarModalOpen(false);
            setSelectedBlogForSimilar(null);
          }}
          sourceId={selectedBlogForSimilar}
          sourceType="blog"
        />
      )}
    </div>
  );
};

export default ComparisonBlogList;
